<script setup lang="ts">
import StatusBadge from '@/components/common/StatusBadge.vue'
import RcMinerEditableDetail from '@/components/rc/miner-repairs/repairs-sidebar/RcMinerEditableDetail.vue'
import { DEFAULT_MINER_SN_THRESHOLD } from '@/config/rcapp.config'
import { refreshMinerWarranty } from '@/services/rc.service'
import type { IMinerDetails } from '@/types/rc/miners'
import { IndividualPart } from '@/types/rc/miners/PartType'
import { Icon } from '@iconify/vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useTimeout } from '@vueuse/core'
import { ElMessage } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, onBeforeMount, ref } from 'vue'
import RcDisplaySerialNumber from '../common/RcDisplaySerialNumber.vue'
import MinersConsolidationDialog from './consolidation/MinersConsolidationDialog.vue'
import { NotifyFail, NotifyInfo, NotifySuccess } from '@/helpers/core/notifications'

const macAddressStyle = 'width: 168px'

const queryClient = useQueryClient()
const { ready, start, stop } = useTimeout(3500, { controls: true })

const props = defineProps<{ minerDetails: IMinerDetails }>()
const emit = defineEmits(['openHistoryDialog'])

const showConsolidationDialog = ref(false)

const isRefreshingWarranty = ref<boolean>(false)

const expirationDate = computed<DateTime | null>(() => {
  if (props.minerDetails.oem_warranty_expiration_date) {
    return DateTime.fromISO(props.minerDetails.oem_warranty_expiration_date, { zone: 'UTC' })
  }
  return null
})

const warrantyStatus = computed<string>(() => {
  if (!expirationDate.value) return 'Unknown'
  if (expirationDate.value < DateTime.now()) return 'Expired'
  return 'Active'
})

/**
 * Refresh warranty status (with mock 2-second loading time to cirumvent BITMAIN's rate limit).
 */
const refreshWarrantyStatus = async () => {
  if (!ready.value) {
    ElMessage.info({
      message: 'Please wait a few seconds before refreshing warranty status again.',
      grouping: true,
    })
    return
  }

  isRefreshingWarranty.value = true
  start() // Start 3.5-second delay timer
  try {
    const minerData = await refreshMinerWarranty(props.minerDetails.id)

    if (!minerData.oem_warranty_expiration_date) {
      NotifyInfo(`Couldn't find expiration date for ${props.minerDetails.sn}`)
      return
    }

    await queryClient.invalidateQueries({ queryKey: ['miner-details', props.minerDetails.id] })
    NotifySuccess('Warranty status updated.')
  } catch (e: any) {
    NotifyFail(e.message)
  } finally {
    isRefreshingWarranty.value = false
  }
}

/**
 * Kills timeout before unmount.
 */
onBeforeMount(() => {
  stop()
})
</script>

<template>
  <div class="title">
    <h2>
      <span>{{ minerDetails.model }} #</span>
      <rc-display-serial-number :sn="minerDetails.sn ?? 'N/A'" :threshold="DEFAULT_MINER_SN_THRESHOLD" />
    </h2>
  </div>
  <MinersConsolidationDialog
    v-model="showConsolidationDialog"
    :work-order="props.minerDetails.work_order_number"
    :customer-id="props.minerDetails.customer.id.toString()"
    :preset1="props.minerDetails.id"
  />
  <el-main class="table-header">
    <el-row class="py-2" :gutter="8">
      <el-col :span="8">
        <div class="table-header__item">
          <span class="table-header__item__title">OEM Warranty</span>
          <div>
            <el-tooltip
              placement="bottom-start"
              :content="`Expiration Date: ${expirationDate?.toFormat('MM/dd/yyyy')}`"
              :disabled="!expirationDate"
            >
              <div class="inline-flex gap-2 items-center">
                <status-badge :status="warrantyStatus" />
                <el-button
                  v-if="warrantyStatus === 'Unknown'"
                  class="cursor-pointer"
                  link
                  @click="refreshWarrantyStatus"
                >
                  <Icon
                    :icon="isRefreshingWarranty ? 'eos-icons:three-dots-loading' : 'material-symbols:refresh'"
                    :height="20"
                    :inline="true"
                  />
                </el-button>
              </div>
            </el-tooltip>
          </div>
        </div>
      </el-col>
      <el-col :span="8" class="table-header__item">
        <span class="table-header__item__title">MAC Address</span>
        <rc-miner-editable-detail
          :style="macAddressStyle"
          prop="mac_address"
          :miner-id="minerDetails.id"
          :part-type="IndividualPart.Miner"
          :val="minerDetails.mac_address"
          clearable
        />
      </el-col>
      <el-col :span="8" class="table-header__item">
        <div class="table-header__item">
          <span class="table-header__item__title">History</span>
          <el-button class="copy-button" @click="emit('openHistoryDialog')">
            <span class="flex items-center gap-2">
              <icon icon="material-symbols:history" :height="20" />
              History
            </span>
          </el-button>
        </div>
      </el-col>
    </el-row>
    <el-row class="py-2" :gutter="8">
      <el-col :span="8" class="table-header__item">
        <span class="table-header__item__title">Inbound</span>
        <div v-if="minerDetails.inbound_container">
          <el-tooltip placement="bottom-start" :content="minerDetails.inbound_container.number">
            <router-link
              :to="{
                name: 'rc-logistics-container-details',
                params: { containerID: minerDetails.inbound_container.id },
              }"
            >
              <el-button link :hover="minerDetails.inbound_container.number">
                <div class="inline-flex gap-2 items-center">
                  {{
                    minerDetails.inbound_container.number.length > 5
                      ? `...${minerDetails.inbound_container.number.slice(-5)}`
                      : minerDetails.inbound_container.number
                  }}
                  <Icon icon="material-symbols:open-in-new" :height="18" />
                </div>
              </el-button>
            </router-link>
          </el-tooltip>
        </div>
      </el-col>
      <el-col :span="8" class="table-header__item">
        <span class="table-header__item__title">Outbound</span>
        <div v-if="minerDetails.outbound_container">
          <el-tooltip placement="bottom-start" :content="minerDetails.outbound_container.number">
            <RouterLink
              :to="{
                name: 'rc-logistics-container-details',
                params: { containerID: minerDetails.outbound_container.id },
              }"
            >
              <el-button link>
                <div class="inline-flex gap-2 items-center">
                  {{
                    minerDetails.outbound_container.number.length > 5
                      ? `...${minerDetails.outbound_container.number.slice(-5)}`
                      : minerDetails.outbound_container.number
                  }}
                  <Icon icon="material-symbols:open-in-new" :height="18" />
                </div>
              </el-button>
            </RouterLink>
          </el-tooltip>
        </div>
      </el-col>
      <el-col :span="8" class="table-header__item">
        <div class="table-header__item">
          <span class="table-header__item__title">Consolidation</span>
          <el-button @click="showConsolidationDialog = true">
            <span class="flex items-center gap-2">
              <icon icon="material-symbols:compare-arrows" :height="20" />
              Consolidation
            </span>
          </el-button>
        </div>
      </el-col>
    </el-row>
  </el-main>
</template>

<style lang="scss">
.title {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-spacing-sm);
}

.table-header {
  padding: 0;
  overflow: hidden;

  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-grow: 1;
    gap: var(--spacing-spacing-xs);

    &__title {
      color: var(--text-tertiary);
      font-weight: 500;
      line-height: 20px;
    }

    &__value {
      color: var(--text-primary);
      font-weight: 400;
      line-height: 20px;
    }
  }
}

.el-button.is-link {
  padding: 0;
}
</style>
